import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  SUPPORT_EMAIL,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
  SUPPORT_PHONE_IN_DISPLAY,
  SUPPORT_PHONE_IN_TEL,
} from '../data/legalContact';

/** Public page — no admin login required. Matches public/privacy.html for App Store review. */
function PrivacyPolicyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy · Saarthi';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Saarthi privacy policy. How the Saarthi taxi rider app collects, uses, and shares email, phone, location, photos, trip data, and payment tokens.'
      );
    }
  }, []);

  return (
    <div className="saarthi-privacy">
      <header className="saarthi-privacy__header">
        <div className="saarthi-privacy__wrap">
          <div className="saarthi-privacy__mark" aria-hidden="true">
            स
          </div>
          <h1>Saarthi Privacy Policy</h1>
          <p className="saarthi-privacy__subtitle">Taxi rider app · com.sarathi.rider</p>
          <nav className="saarthi-privacy__top-links" aria-label="Site">
            <Link to="/">Home</Link>
            <Link to="/support">Support</Link>
          </nav>
        </div>
      </header>
      <main className="saarthi-privacy__main">
        <div className="saarthi-privacy__wrap">
          <article className="saarthi-privacy__article">
            <p className="saarthi-privacy__meta">
              Effective 1 October 2026. This policy is for the Saarthi rider app on iPhone and Android.
            </p>
            <p>
              Saarthi (“Saarthi,” “we,” “us”) is a taxi rider app. You use it to book a city ride, daily commute, or
              outstation taxi, pay, and follow the trip. This page explains what personal data we collect, why we
              collect it, and who we share it with. We <strong>do collect personal data</strong>. We do not sell it. We
              do not use App Tracking Transparency because we do not advertise or track you across other companies’
              apps or websites.
            </p>

            <h2>1. Data we collect</h2>
            <p>We collect the following, which is linked to your Saarthi account:</p>
            <ul>
              <li>
                <strong>Contact info:</strong> name, email address, and phone number (if you add one). We send a
                one-time sign-in code to your email.
              </li>
              <li>
                <strong>Location:</strong> precise location when you allow it, so we can set pickup, show nearby taxis,
                estimate routes, and share live trip progress with you and your driver. You can still type an address if
                you deny location.
              </li>
              <li>
                <strong>Photos and ID:</strong> optional profile photo. If you apply to drive or upload self-drive
                documents, we also store government ID (front and back) and licence photos.
              </li>
              <li>
                <strong>Trip data:</strong> pickup and drop-off, stops, times, vehicle and driver assigned, fare,
                payment method chosen (card, UPI, or cash), ratings, cancellations, SOS alerts, and in-trip chat.
              </li>
              <li>
                <strong>Payment tokens:</strong> when you pay by card or save a card, Stripe stores a payment token (and
                last four digits / brand). Saarthi does <strong>not</strong> store full card numbers or CVV.
              </li>
              <li>
                <strong>Identifiers:</strong> your Saarthi user ID and a device push token so we can notify you about
                the ride (driver assigned, arriving, chat, and similar).
              </li>
              <li>
                <strong>Safety contacts:</strong> names and phone numbers of trusted contacts you add for SOS.
              </li>
            </ul>

            <h2>2. How we use it</h2>
            <p>We use this data for app functionality only:</p>
            <ul>
              <li>Create and sign in to your account</li>
              <li>Book, dispatch, track, and complete rides</li>
              <li>Charge fares and wait-time through Stripe, or record a cash trip</li>
              <li>Show maps, search places, and calculate routes</li>
              <li>Send ride and chat notifications</li>
              <li>Safety features (SOS, trusted contacts, support)</li>
              <li>Customer support and fraud / dispute review</li>
              <li>Send trip receipts or invoices to your email when a trip completes</li>
            </ul>
            <p>
              We do not use this data for third-party advertising, and we do not track you across other apps for ads.
            </p>

            <h2>3. Who we share it with</h2>
            <p>We share data with processors who run the app for us. They may only use it to provide that service:</p>
            <ul>
              <li>
                <strong>Supabase</strong> — accounts, database, file storage (photos/ID), and backend functions.
              </li>
              <li>
                <strong>Stripe</strong> — card and wallet payments. Stripe receives payment tokens, not your full card
                number through Saarthi.
              </li>
              <li>
                <strong>Google Maps</strong> — place search, geocoding, directions, and map display. Pickup and drop-off
                coordinates or addresses are sent to Google to return a route or place.
              </li>
              <li>
                <strong>Expo and Apple Push Notification service</strong> — ride and chat alerts to your device using
                your push token.
              </li>
            </ul>
            <p>
              During an active trip we also share needed trip details with the assigned driver (pickup, drop-off, first
              name, and similar) so they can complete the ride. If you use SOS, we may share trip location with support
              or emergency contacts you chose. We may disclose information if the law requires it.
            </p>

            <h2>4. Permissions on your phone</h2>
            <ul>
              <li>
                <strong>Location</strong> — used while the app is in use for pickup and live trip maps. We do not need
                always-on background location for the rider app.
              </li>
              <li>
                <strong>Camera / photos</strong> — only if you take or choose a profile photo or ID document.
              </li>
              <li>
                <strong>Notifications</strong> — optional; used for ride updates.
              </li>
            </ul>

            <h2>5. Retention and your choices</h2>
            <p>
              We keep account and trip records while you have an account and for a reasonable period after (for
              receipts, disputes, and legal requirements). You can edit name, phone, photo, and cards in Profile. To
              request a copy or deletion of your account data, email us. Some trip records may be kept where we must
              retain them for law, safety, or payment disputes.
            </p>

            <h2>6. Children</h2>
            <p>
              Saarthi is a taxi booking app for adults booking their own rides. It is not directed at children and is
              not a kids’ app. We do not knowingly collect data from children under 13.
            </p>

            <h2>7. Contact</h2>
            <p>
              Privacy questions and deletion requests:
              <br />
              Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              <br />
              United States support:{' '}
              <a href={`tel:${SUPPORT_PHONE_TEL}`}>+1 {SUPPORT_PHONE_DISPLAY}</a>
              <br />
              India support:{' '}
              <a href={`tel:${SUPPORT_PHONE_IN_TEL}`}>{SUPPORT_PHONE_IN_DISPLAY}</a>
            </p>
            <p>
              You can also open <strong>Safety &amp; support</strong> in the Saarthi app.
            </p>
            <footer className="saarthi-privacy__footer">
              App Store listing name: Saarthi. Support page:{' '}
              <Link to="/support">sarthi-taxi-hiring.netlify.app/support</Link>.
            </footer>
          </article>
        </div>
      </main>
    </div>
  );
}

export default PrivacyPolicyPage;
